import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Icon from '../components/atoms/Icon';
import InterviewContinuationBanner from '../components/InterviewContinuationBanner';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

const PlacementCompanyPage = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [positions, setPositions] = useState([]);
  const [experienceFilter, setExperienceFilter] = useState('');

  const session = SessionManager.getCurrentSession();
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;

  useEffect(() => {
    loadCompanyData();
  }, [companyId]);

  const loadCompanyData = async () => {
    try {
      setLoading(true);
      
      const headers = {};
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      
      // Load company details
      const companyResponse = await fetch(
        `${apiUrl}/placement-training/companies/${companyId}`,
        { headers }
      );

      if (companyResponse.ok) {
        const companyData = await companyResponse.json();
        setCompany(companyData.company);
      }

      // Load positions
      const positionsResponse = await fetch(
        `${apiUrl}/placement-training/companies/${companyId}/positions`,
        { headers }
      );

      if (positionsResponse.ok) {
        const positionsData = await positionsResponse.json();
        setPositions(positionsData.positions || []);
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPositions = experienceFilter
    ? positions.filter(p => p.experience_level === experienceFilter)
    : positions;

  const experienceLevels = [...new Set(positions.map(p => p.experience_level).filter(Boolean))];

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-gray-50">
      <InterviewContinuationBanner />
      
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <LandingHeader />
      </div>

      <div className="max-w-7xl mx-auto w-full p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          </div>
        ) : !company ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Icon name="error_outline" size={48} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Company not found</p>
            <button
              onClick={() => navigate('/placement-training')}
              className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              Back to Companies
            </button>
          </div>
        ) : (
          <div>
            {/* Back Button */}
            <button
              onClick={() => navigate('/placement-training')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
            >
              <Icon name="arrow_back" size={20} />
              <span>Back to Companies</span>
            </button>

            {/* Company Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Company Logo */}
            <div className="flex-shrink-0">
              <div className="w-32 h-32 bg-gray-50 rounded-xl flex items-center justify-center p-4">
                {company.company_logo_url ? (
                  <img
                    src={company.company_logo_url}
                    alt={company.company_name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">
                      {company.company_name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Company Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {company.company_name}
              </h1>
              
              {company.company_type && (
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-4">
                  {company.company_type}
                </span>
              )}

              <p className="text-gray-600 mb-4 leading-relaxed">
                {company.company_description || 'Leading global company providing innovative solutions.'}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {company.headquarters && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Icon name="location_on" size={20} className="text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Headquarters</div>
                      <div className="font-medium">{company.headquarters}</div>
                    </div>
                  </div>
                )}
                
                {company.employee_count && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Icon name="people" size={20} className="text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Employees</div>
                      <div className="font-medium">{company.employee_count}</div>
                    </div>
                  </div>
                )}
                
                {company.founded_year && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Icon name="calendar_today" size={20} className="text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Founded</div>
                      <div className="font-medium">{company.founded_year}</div>
                    </div>
                  </div>
                )}
              </div>

              {company.company_website && (
                <a
                  href={company.company_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 text-blue-600 hover:text-blue-700"
                >
                  <Icon name="language" size={18} />
                  <span>Visit Website</span>
                  <Icon name="open_in_new" size={16} />
                </a>
              )}
            </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="work" size={24} className="text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{positions.length}</div>
                <div className="text-sm text-gray-600">Open Positions</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Icon name="trending_up" size={24} className="text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{company.total_assessments || 0}</div>
                <div className="text-sm text-gray-600">Total Attempts</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Icon name="verified" size={24} className="text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {positions.reduce((sum, p) => sum + (p.total_passed || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">Certificates Issued</div>
              </div>
                </div>
              </div>
            </div>

            {/* Filter */}
            {experienceLevels.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Filter by Experience:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setExperienceFilter('')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    experienceFilter === ''
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                {experienceLevels.map(level => (
                  <button
                    key={level}
                    onClick={() => setExperienceFilter(level)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      experienceFilter === level
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {level}
                  </button>
                ))}
                  </div>
                </div>
              </div>
            )}

            {/* Positions */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Positions</h2>
              
              <div className="grid grid-cols-1 gap-4">
                {filteredPositions.map((position) => (
                  <div
                    key={position.id}
                    onClick={() => navigate(`/placement-training/position/${position.id}`)}
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-teal-300 transition-all cursor-pointer group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                          {position.position_title}
                        </h3>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {position.experience_level && (
                            <span className="px-3 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full">
                              {position.experience_level}
                            </span>
                          )}
                          {position.salary_range && (
                            <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                              {position.salary_range}
                            </span>
                          )}
                          <span className="px-3 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full">
                            {position.total_rounds} Rounds
                          </span>
                        </div>

                        {position.position_description && (
                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                            {position.position_description}
                          </p>
                        )}

                        {position.skills_required && position.skills_required.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {position.skills_required.slice(0, 5).map((skill, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                              >
                                {skill}
                              </span>
                            ))}
                            {position.skills_required.length > 5 && (
                              <span className="px-2 py-1 text-gray-500 text-xs">
                                +{position.skills_required.length - 5} more
                              </span>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Icon name="people" size={16} />
                            <span>{position.total_attempts || 0} attempts</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="verified" size={16} />
                            <span>{position.total_passed || 0} passed</span>
                          </div>
                          {position.passing_threshold_percentage && (
                            <div className="flex items-center gap-1">
                              <Icon name="flag" size={16} />
                              <span>{position.passing_threshold_percentage}% to pass</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="ml-4">
                        <Icon name="chevron_right" size={24} className="text-gray-400 group-hover:text-teal-600 transition-colors" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredPositions.length === 0 && (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                  <Icon name="work_off" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No positions found matching your filter</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacementCompanyPage;

